import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { FeedsService, RSSSourceWithCounts, DiscoveredFeed } from '../../services/feedsService';
import PageHeader from '../common/PageHeader';
import './FeedSourceManagement.css';

interface FeedSourceManagementProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const FeedSourceManagement: React.FC<FeedSourceManagementProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const navigate = useNavigate();
  const [sources, setSources] = useState<RSSSourceWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Add source states
  const [inputUrl, setInputUrl] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [discoveredFeeds, setDiscoveredFeeds] = useState<DiscoveredFeed[]>([]);
  const [adding, setAdding] = useState(false);
  
  // Edit states
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  
  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  
  // Refresh states
  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  useEffect(() => {
    loadSources();
  }, []);

  const loadSources = async () => {
    try {
      setLoading(true);
      setError(null);
      const sourcesData = await FeedsService.getSources();
      setSources(sourcesData);
    } catch (err) {
      console.error('Error loading sources:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sources');
    } finally {
      setLoading(false);
    }
  };

  const handleDiscover = async () => {
    if (!inputUrl.trim()) {
      setError('Please enter a website URL');
      return;
    }

    try {
      setDiscovering(true);
      setError(null);
      setDiscoveredFeeds([]);
      
      const feeds = await FeedsService.discoverFeeds(inputUrl.trim());
      setDiscoveredFeeds(feeds);
      
      if (feeds.length === 0) {
        setError('No RSS feeds found at this URL. Try adding the RSS feed URL directly.');
      }
    } catch (err) {
      console.error('Error discovering feeds:', err);
      setError(err instanceof Error ? err.message : 'Failed to discover feeds');
    } finally {
      setDiscovering(false);
    }
  };

  const handleAddSource = async (feedUrl?: string, feedTitle?: string) => {
    const urlToAdd = feedUrl || inputUrl.trim();
    const nameToUse = feedTitle || sourceName.trim() || undefined;

    if (!urlToAdd) {
      setError('Please enter a feed URL');
      return;
    }

    try {
      setAdding(true);
      setError(null);
      
      const result = await FeedsService.createSource(urlToAdd, nameToUse);
      
      // Reset form
      setInputUrl('');
      setSourceName('');
      setDiscoveredFeeds([]);
      
      // Reload sources
      await loadSources();
      
      setError(null);
      // Show success message
      alert(`Successfully added "${result.source.source_name}" with ${result.itemsInserted} items`);
    } catch (err) {
      console.error('Error adding source:', err);
      setError(err instanceof Error ? err.message : 'Failed to add source');
    } finally {
      setAdding(false);
    }
  };

  const handleStartEdit = (source: RSSSourceWithCounts) => {
    setEditingId(source.source_id);
    setEditName(source.source_name);
  };

  const handleSaveEdit = async (sourceId: string) => {
    try {
      await FeedsService.updateSource(sourceId, { sourceName: editName.trim() });
      setEditingId(null);
      setEditName('');
      await loadSources();
    } catch (err) {
      console.error('Error updating source:', err);
      setError(err instanceof Error ? err.message : 'Failed to update source');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleToggleActive = async (sourceId: string, isActive: boolean) => {
    try {
      await FeedsService.updateSource(sourceId, { isActive: !isActive });
      await loadSources();
    } catch (err) {
      console.error('Error toggling source:', err);
      setError(err instanceof Error ? err.message : 'Failed to toggle source');
    }
  };

  const handleDelete = async (sourceId: string) => {
    try {
      await FeedsService.deleteSource(sourceId);
      setDeleteConfirmId(null);
      await loadSources();
    } catch (err) {
      console.error('Error deleting source:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete source');
    }
  };

  const handleRefresh = async (sourceId: string) => {
    try {
      setRefreshingId(sourceId);
      setError(null);
      
      const result = await FeedsService.refreshSource(sourceId);
      
      await loadSources();
      
      alert(`Refreshed: ${result.newItems} new items, ${result.skippedDuplicates} duplicates skipped`);
    } catch (err) {
      console.error('Error refreshing source:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh source');
    } finally {
      setRefreshingId(null);
    }
  };

  const handleBackToFeeds = () => {
    navigate('/feeds');
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="feed-source-management-page">
      <PageHeader 
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">📡 Manage RSS Sources</h1>}
        leftActions={
          <button 
            className="page-header-icon-btn feed-source-back-btn"
            onClick={handleBackToFeeds}
          >
            ← Back
          </button>
        }
      />

      <div className="feed-source-management-content">
        {/* Add New Source Section */}
        <div className="add-source-section">
          <h2>Add New Source</h2>
          
          {error && (
            <div className="source-error-message">
              {error}
            </div>
          )}

          <div className="add-source-form">
            <div className="add-source-input-group">
              <input
                type="text"
                className="add-source-input"
                placeholder="Enter RSS feed URL or website URL..."
                value={inputUrl}
                onChange={(e) => setInputUrl(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleDiscover();
                  }
                }}
              />
              <input
                type="text"
                className="add-source-input"
                placeholder="Custom name (optional)"
                value={sourceName}
                onChange={(e) => setSourceName(e.target.value)}
              />
            </div>

            <div className="add-source-buttons">
              <button
                className="add-source-btn discover-btn"
                onClick={handleDiscover}
                disabled={discovering || adding}
              >
                {discovering ? 'Discovering...' : '🔍 Discover Feeds'}
              </button>
              <button
                className="add-source-btn add-btn"
                onClick={() => handleAddSource()}
                disabled={discovering || adding}
              >
                {adding ? 'Adding...' : '➕ Add Feed Directly'}
              </button>
            </div>
          </div>

          {/* Discovered Feeds */}
          {discoveredFeeds.length > 0 && (
            <div className="discovered-feeds">
              <h3>Discovered Feeds:</h3>
              <div className="discovered-feeds-list">
                {discoveredFeeds.map((feed, index) => (
                  <div key={index} className="discovered-feed-item">
                    <div className="discovered-feed-info">
                      <div className="discovered-feed-title">{feed.title}</div>
                      <div className="discovered-feed-url">{feed.url}</div>
                      <div className="discovered-feed-meta">
                        {feed.itemCount} items • Found by: {feed.foundBy}
                      </div>
                    </div>
                    <button
                      className="discovered-feed-add-btn"
                      onClick={() => handleAddSource(feed.url, feed.title)}
                      disabled={adding}
                    >
                      Add
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sources List */}
        <div className="sources-list-section">
          <h2>Your Sources ({sources.length})</h2>

          {loading ? (
            <div className="sources-loading">
              <div className="loading-spinner"></div>
              <p>Loading sources...</p>
            </div>
          ) : sources.length === 0 ? (
            <div className="sources-empty">
              <p>No RSS sources added yet. Add your first source above!</p>
            </div>
          ) : (
            <div className="sources-list">
              {sources.map(source => (
                <div 
                  key={source.source_id} 
                  className={`source-card ${!source.is_active ? 'inactive' : ''}`}
                >
                  <div className="source-card-header">
                    {editingId === source.source_id ? (
                      <input
                        type="text"
                        className="source-edit-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                      />
                    ) : (
                      <h3 className="source-card-title">
                        {source.source_name}
                        {!source.is_active && <span className="inactive-badge">Inactive</span>}
                      </h3>
                    )}
                  </div>

                  <div className="source-card-info">
                    <div className="source-card-url">{source.source_url}</div>
                    {source.site_url && (
                      <div className="source-card-site">
                        <a href={source.site_url} target="_blank" rel="noopener noreferrer">
                          {source.site_url}
                        </a>
                      </div>
                    )}
                    {source.description && (
                      <div className="source-card-description">{source.description}</div>
                    )}
                  </div>

                  <div className="source-card-stats">
                    <span className="source-stat">
                      <strong>{source.item_count}</strong> items
                    </span>
                    <span className="source-stat">
                      <strong>{source.unread_count}</strong> unread
                    </span>
                    <span className="source-stat">
                      Last fetched: {formatDate(source.last_fetched_at)}
                    </span>
                  </div>

                  {source.fetch_error && (
                    <div className="source-error">
                      Error: {source.fetch_error}
                    </div>
                  )}

                  <div className="source-card-actions">
                    {editingId === source.source_id ? (
                      <>
                        <button
                          className="source-action-btn save-btn"
                          onClick={() => handleSaveEdit(source.source_id)}
                        >
                          Save
                        </button>
                        <button
                          className="source-action-btn cancel-btn"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="source-action-btn"
                          onClick={() => handleStartEdit(source)}
                        >
                          Edit Name
                        </button>
                        <button
                          className="source-action-btn"
                          onClick={() => handleToggleActive(source.source_id, source.is_active)}
                        >
                          {source.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="source-action-btn refresh-btn"
                          onClick={() => handleRefresh(source.source_id)}
                          disabled={refreshingId === source.source_id}
                        >
                          {refreshingId === source.source_id ? '🔄 Refreshing...' : '🔄 Refresh'}
                        </button>
                        {deleteConfirmId === source.source_id ? (
                          <div className="source-delete-confirm">
                            <span>Delete?</span>
                            <button
                              className="source-action-btn confirm-btn"
                              onClick={() => handleDelete(source.source_id)}
                            >
                              Yes
                            </button>
                            <button
                              className="source-action-btn cancel-btn"
                              onClick={() => setDeleteConfirmId(null)}
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            className="source-action-btn delete-btn"
                            onClick={() => setDeleteConfirmId(source.source_id)}
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedSourceManagement;

